// src/pages/misc/onboarding/OnboardingReturnPage.tsx
// [HANDOFF] THE RETURN FROM STRIPE (/onboarding/estado - routed at WU-145's switch, P-5; WU-142 UNIT 8 RES2, docs/manifest/WU-142.md
//   SESSION 39 RECORD UNIT 8 THE DESIGN D-R4 to D-R6, D-R8 and D-R9). Stripe sends the visitor back with `status` and
//   `application_id`, the names the server writes. An id that is no application id: not found, the login its one button.
//   status=cancelled: back to the checkout, the draft intact and nothing paid. Any other status is never read as paid: the server's
//   state (P5) is read at mount and every POLL_EVERY_MS while the payment or the account is on its way, at most POLL_READS reads,
//   and the page shows what the server answers - waiting, activating, ready (the server's login), failed (the server's support
//   message and the WhatsApp line), superseded (the server's login) - or, past the last read, still processing. A refusal keeps the
//   last view and the cadence (the rate limit with the server's message); not found ends the reads. At the first answer that the
//   payment is in (paid or active) the six browser keys of the onboarding (continuousPath.ts section 5) are removed, each by its
//   owner's name - for a per-seat application only. Both lanes are read through the one view map, as P5 answers them alike: a
//   legacy application shows its state's view (WU-145 SESSION 4 RECORD THE SELF-STAMPS 11). A screen he did not draw, in his
//   style, inside his OnboardingFrame.
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../contexts/languages/LanguageContext';
import { LENGTH_KEY, publicJourney } from './continuousPath';
import OnboardingFrame from './OnboardingFrame';
import { PERSON_KEY } from './OnboardingAccountPage';
import { SETUP_STEP_DONE } from './StructureBuilderPage';
import { fetchPerSeatApplicationState, type PerSeatState } from './perSeatApi';
import { clearStructureDraft, loadStructureDraft } from './structureDraft';
import { clearOrgSetup } from '../../settings/create-organization/orgSetup';
import './builder.css';
import '../../settings/create-organization/orgFlow.css';
import '../../auth/auth.css';

/** An application id as the server writes it (a uuid); any other value names no application. */
export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** D-R5: a read every five seconds, at most 24 reads - two minutes, inside the edges' 30 reads per 600 s. */
export const POLL_EVERY_MS = 5000;
export const POLL_READS = 24;
export const LOGIN_URL = 'https://app.ventaje.com/login';
const WHATSAPP_URL = 'https://wa.me/529995427336';
const CHECKOUT_ROUTE = '/onboarding/checkout';
const RESUME_ROUTE = '/onboarding/continuar';

type View =
  | { kind: 'loading' }
  | { kind: 'waiting' }
  | { kind: 'activating' }
  | { kind: 'ready'; loginUrl: string }
  | { kind: 'failed'; supportMessage?: string }
  | { kind: 'superseded'; loginUrl: string }
  | { kind: 'notFound' }
  | { kind: 'stillProcessing' };

/** The view of one answer: the state as stored, never narrowed - a state the page does not name waits, never read as paid. */
function viewOf(answer: PerSeatState): View {
  switch (answer.state) {
    case 'pending_payment':
      return { kind: 'waiting' };
    case 'paid':
      return { kind: 'activating' };
    case 'active':
      return { kind: 'ready', loginUrl: answer.loginUrl };
    case 'activation_failed':
    case 'creation_failed':
      return { kind: 'failed', supportMessage: answer.supportMessage };
    case 'superseded':
      return { kind: 'superseded', loginUrl: answer.loginUrl };
    default:
      return { kind: 'waiting' };
  }
}

/** The views the poll goes on from: the first read not yet answered, the payment on its way and the account on its way. */
function polls(view: View): boolean {
  return view.kind === 'loading' || view.kind === 'waiting' || view.kind === 'activating';
}

/** D-R6 (S-16): the six keys of continuousPath.ts section 5, each removed by its owner's name; the password was never stored. */
function clearOnboarding(): void {
  clearStructureDraft();
  clearOrgSetup();
  try {
    sessionStorage.removeItem(PERSON_KEY);
    sessionStorage.removeItem(SETUP_STEP_DONE);
    sessionStorage.removeItem(LENGTH_KEY);
  } catch {
    /* storage unavailable: nothing was kept */
  }
}

const OnboardingReturnPage: React.FC = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const given = params.get('application_id') ?? '';
  const applicationId = UUID_PATTERN.test(given) ? given : null;
  const cancelled = params.get('status') === 'cancelled';
  const [view, setView] = useState<View>(applicationId === null ? { kind: 'notFound' } : { kind: 'loading' });
  const [rateLimit, setRateLimit] = useState('');
  // The rising bar's place, read once: the clear below removes the structure length it reads.
  const [progress] = useState(() => publicJourney(loadStructureDraft().path, 'checkout'));
  const cleared = useRef(false);

  useEffect(() => {
    if (applicationId === null || cancelled) return undefined;
    let alive = true;
    let reads = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // D-R5 (self-stamp 112): a setTimeout chain - one read in flight, the next scheduled only after its answer, cleared on unmount.
    const read = async (): Promise<void> => {
      reads += 1;
      const result = await fetchPerSeatApplicationState(applicationId);
      if (!alive) return;
      let next: View | null = null;
      if (result.ok) {
        next = viewOf(result.value);
        setRateLimit('');
        const paidIn = result.value.state === 'paid' || result.value.state === 'active';
        if (paidIn && result.value.lane === 'per_seat' && !cleared.current) {
          cleared.current = true;
          clearOnboarding();
        }
      } else if (result.refusal.code === 'APPLICATION_NOT_FOUND') {
        next = { kind: 'notFound' };
      } else if (result.refusal.code === 'RATE_LIMITED') {
        setRateLimit(result.refusal.message);
      }
      if (next !== null) setView(next);
      if (next !== null && !polls(next)) return;
      if (reads >= POLL_READS) {
        setView({ kind: 'stillProcessing' });
        return;
      }
      timer = setTimeout(() => {
        void read();
      }, POLL_EVERY_MS);
    };
    void read();
    return () => {
      alive = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [applicationId, cancelled]);

  if (applicationId !== null && cancelled) return <Navigate to={CHECKOUT_ROUTE} replace />;

  const gold = 'btn-gold inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl text-sm font-bold';
  const resume = (
    <Link className={gold} to={`${RESUME_ROUTE}?resume=${applicationId ?? ''}`}>
      {t('publicOnboarding.return.resume')}
    </Link>
  );
  const whatsapp = (
    <p className="ob-flow-note">
      <a className="underline" href={WHATSAPP_URL} target="_blank" rel="noreferrer">
        {t('publicOnboarding.return.whatsapp')}
      </a>
    </p>
  );
  let title: React.ReactNode;
  let action: React.ReactNode = null;
  switch (view.kind) {
    case 'loading':
      title = t('publicOnboarding.return.loading');
      break;
    case 'waiting':
      title = t('publicOnboarding.return.waiting');
      break;
    case 'activating':
      title = t('publicOnboarding.return.activating');
      break;
    case 'ready':
      title = t('publicOnboarding.return.ready');
      action = (
        <a className={gold} href={view.loginUrl}>
          {t('publicOnboarding.return.login')}
        </a>
      );
      break;
    case 'failed':
      title = view.supportMessage ?? t('publicOnboarding.return.failed');
      action = whatsapp;
      break;
    case 'superseded':
      // SESSION 44 RECORD THE SUPERSEDED LOOP RESOLVED (self-stamps 141 and 142): the server's login resolves the newest
      // application, which no answer in hand names - the resume route would only send him back here.
      title = t('publicOnboarding.return.superseded');
      action = (
        <a className={gold} href={view.loginUrl}>
          {t('publicOnboarding.return.login')}
        </a>
      );
      break;
    case 'notFound':
      title = t('publicOnboarding.return.notFound');
      action = (
        <a className={gold} href={LOGIN_URL}>
          {t('publicOnboarding.return.login')}
        </a>
      );
      break;
    default:
      title = t('publicOnboarding.return.stillProcessing');
      action = (
        <>
          {resume}
          {whatsapp}
        </>
      );
  }

  return (
    <OnboardingFrame step={progress.step} total={progress.total} band="card">
      <div className="ob-flow-copy">
        <h1 className="ob-flow-title">{title}</h1>
        {rateLimit !== '' && polls(view) ? (
          <p className="auth-err">
            <AlertTriangle size={12} />
            {rateLimit}
          </p>
        ) : null}
      </div>
      {action === null ? null : <div className="org-float">{action}</div>}
    </OnboardingFrame>
  );
};

export default OnboardingReturnPage;
